// catalog.ts — typed client for the shopify-catalog Edge Function: live price +
// stock for the current configuration. Used only in Shopify-sourced mode; on any
// failure the caller keeps the made-to-order computePrice (graceful degradation),
// so this never throws — it resolves to an ok:false response instead.

import type { ConfigState } from "@/types";

const PROXY = "https://vmvbvbrcdphknjibekgg.supabase.co/functions/v1/shopify-catalog";

export interface CatalogLine {
  pieceId: string;
  variantId: string;
  quantity: number;
  title: string;
  price: number;
  available: boolean;
  inventory: number | null;
  found: boolean;
}

/** Per-variant stock, for greying options the shopper hasn't picked yet. */
export interface VariantAvailability {
  available: boolean;
  inventory: number | null;
}

/** numeric variantId -> availability, for EVERY variant in the binding. */
export type AvailMap = Record<string, VariantAvailability>;

export interface CatalogOk {
  ok: true;
  source: "shopify";
  currency: string;
  total: number;
  allAvailable: boolean;
  lines: CatalogLine[];
  /** Whole-product stock map (numeric variantId -> availability). Absent key =
   *  unknown = treated as available (fail-open). */
  availability: AvailMap;
}

export interface CatalogMiss {
  ok: false;
  reason: string;
  missing?: string[];
}

export type CatalogResponse = CatalogOk | CatalogMiss;

export async function fetchCatalog(slug: string, state: ConfigState): Promise<CatalogResponse> {
  try {
    const res = await fetch(PROXY, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ slug, state }),
    });
    const data = (await res.json().catch(() => null)) as CatalogResponse | null;
    if (data && typeof data.ok === "boolean") return data;
    return { ok: false, reason: `http-${res.status}` };
  } catch {
    return { ok: false, reason: "network" };
  }
}
